import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarIcon from '@mui/icons-material/Star';
import CommentIcon from '@mui/icons-material/Comment';
import { User } from '@/types/user';

interface ProfileDisplayProps {
  user: User;
  onEdit: () => void;
}

/**
 * Profile display component showing user information and activity stats.
 * Designed with a clean, modern layout similar to Fiverr-style profile cards.
 */
export const ProfileDisplay: React.FC<ProfileDisplayProps> = ({ user, onEdit }) => {
  // Calculate activity counts
  const favoritesCount = user.favorites?.length || 0;
  const ratingsCount = user.ratings?.length || 0;
  const commentsCount = user.comments?.length || 0;

  // Format member since date
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null;

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: 2,
        maxWidth: 800,
        mx: 'auto',
        backgroundColor: 'background.paper',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Profile Picture and Header */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, position: 'relative' }}>
          {/* Profile Picture Placeholder */}
          <Avatar
            sx={{
              width: 120,
              height: 120,
              bgcolor: 'primary.light',
              mb: 2,
              border: '4px solid',
              borderColor: 'background.paper',
              boxShadow: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 60, color: 'primary.main' }} />
          </Avatar>

          {/* Name and Username */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                fontSize: '1.5rem',
                color: 'text.primary',
              }}
            >
              {user.first_name} {user.last_name}
            </Typography>
            <Button
              size="small"
              sx={{
                minWidth: 'auto',
                p: 0.5,
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={onEdit}
            >
              <EditIcon fontSize="small" />
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              mb: 2,
            }}
          >
            @{user.email_address.split('@')[0]}
          </Typography>

          {/* Edit Button */}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              px: 3,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Edit
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Profile Details */}
        <Box sx={{ mb: 3 }}>
          {/* From (Location) */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                }}
              >
                From
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: 'text.primary',
                fontWeight: 400,
              }}
            >
              {user.city && user.country ? `${user.city}, ${user.country}` : user.country || user.city || 'Not specified'}
            </Typography>
          </Box>

          {/* Member Since */}
          {memberSince && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Member since
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  fontWeight: 400,
                }}
              >
                {memberSince}
              </Typography>
            </Box>
          )}

          {/* Email */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.secondary',
              }}
            >
              Email
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: 'text.primary',
                fontWeight: 400,
              }}
            >
              {user.email_address}
            </Typography>
          </Box>

          {/* Role */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.secondary',
              }}
            >
              Role
            </Typography>
            <Chip
              label={user.role === 'admin' ? 'Administrator' : 'User'}
              color={user.role === 'admin' ? 'primary' : 'default'}
              size="small"
              sx={{ height: 24 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Activity Stats */}
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.primary',
              mb: 2.5,
            }}
          >
            Activity
          </Typography>

          {/* Favorites */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FavoriteIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                }}
              >
                Favorites
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              {favoritesCount}
            </Typography>
          </Box>

          {/* Ratings */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                }}
              >
                Ratings
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              {ratingsCount}
            </Typography>
          </Box>

          {/* Comments */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CommentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                }}
              >
                Comments
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              {commentsCount}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
